import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Pause, Play, Ban, RotateCcw, RefreshCw, Trash2, Gift, X, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { StatusBadge } from "../shared/StatusBadge";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";
import { formatCurrency, formatDate } from "../../lib/utils";
import { subscriptionStatusMeta } from "../../lib/statusMaps";

const DAY_LABELS = { sunday: "Sun", monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat" };

// A single label + value slot. In edit mode the value line is replaced by the
// supplied input; the label position never moves, so toggling edit barely shifts
// the layout.
function Field({ label, children, testId, className }) {
  return (
    <div className={className} data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <div className="mt-1 text-sm font-medium text-text-main">{children}</div>
    </div>
  );
}

export function SubscriptionInfoCard({ subscription: s, onSaved, onAction, hasPause, isCurrentlyPaused }) {
  const meta = subscriptionStatusMeta(s.status);
  const isDeleted = s.status === 5;
  const isDeactivated = s.status === 3;
  const isActiveOrPending = s.status === 1 || s.status === 2;
  const canRenew = (s.deliveries_count?.delivery_pending != null && s.deliveries_count.delivery_pending <= 3) || s.status === 4;
  const selectedDays = Object.entries(s.deliver_days || {}).filter(([, v]) => v).map(([k]) => k);
  const deliveredPct = s.deliveries_count?.delivery_total
    ? Math.round(((s.deliveries_count.delivery_total - s.deliveries_count.delivery_pending) / s.deliveries_count.delivery_total) * 100)
    : 0;

  const [editing, setEditing] = useState(false);
  const [showAllAddons, setShowAllAddons] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [outletDays, setOutletDays] = useState({ sunday: 1, monday: 1, tuesday: 1, wednesday: 1, thursday: 1, friday: 1, saturday: 1 });
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState({ loading: false, price: null, end_date: null, deliveries_total: null });
  const debounceRef = useRef(null);
  const optionsLoaded = useRef(false);

  const loadOptions = async () => {
    if (optionsLoaded.current) return;
    const [planRes, addonRes, dayRes] = await Promise.allSettled([
      apiClient.get("/plans/active"),
      apiClient.get("/addons", { params: { limit: 200 } }),
      apiClient.get("/outlet/deliver-days"),
    ]);
    let planOptions = planRes.status === "fulfilled" ? planRes.value.data : [];
    if (!planOptions.some((p) => p.id === s.plan_id)) {
      try { planOptions = [...planOptions, (await apiClient.get(`/plans/${s.plan_id}`)).data]; } catch { /* gone */ }
    }
    setPlans(planOptions);
    setAddons(addonRes.status === "fulfilled" ? addonRes.value.data.items : []);
    if (dayRes.status === "fulfilled") setOutletDays(dayRes.value.data);
    optionsLoaded.current = true;
  };

  const enterEdit = async () => {
    setErrors({});
    setForm({
      plan_id: String(s.plan_id),
      type: String(s.type || "2"),
      status: String(s.status),
      start_date: s.start_date || "",
      day_names: selectedDays,
      addon_ids: (s.addons || []).map((a) => a.id),
      delivery_notes: s.delivery_notes || "",
      internal_notes: s.internal_notes || "",
      packaging_notes: s.packaging_notes || "",
    });
    setPreview({ loading: false, price: s.price, end_date: s.end_date, deliveries_total: s.deliveries_count?.delivery_total ?? null });
    setShowAllAddons(true);
    setEditing(true);
    await loadOptions();
  };

  const cancelEdit = () => { setEditing(false); setForm(null); setShowAllAddons(false); setErrors({}); };

  // Live server preview of price / end date while editing.
  useEffect(() => {
    if (!editing || !form?.plan_id || !form?.start_date) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreview((p) => ({ ...p, loading: true }));
      try {
        const { data } = await apiClient.get("/subscriptions/preview", {
          params: {
            plan_id: form.plan_id, start_date: form.start_date,
            addon_ids: form.addon_ids.join(",") || undefined,
            deliver_days: form.day_names.join(",") || undefined,
          },
        });
        setPreview({ loading: false, price: data.price, end_date: data.end_date, deliveries_total: data.deliveries_total });
      } catch {
        setPreview((p) => ({ ...p, loading: false }));
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [editing, form?.plan_id, form?.start_date, form?.addon_ids, form?.day_names]);

  const toggleDay = (day) => {
    if (!outletDays[day]) return;
    setForm((f) => ({ ...f, day_names: f.day_names.includes(day) ? f.day_names.filter((d) => d !== day) : [...f.day_names, day] }));
  };
  const toggleAddon = (id) => {
    setForm((f) => ({ ...f, addon_ids: f.addon_ids.includes(id) ? f.addon_ids.filter((a) => a !== id) : [...f.addon_ids, id] }));
  };

  const save = async () => {
    if (!form.plan_id || !form.start_date) {
      setErrors({ form: "Plan and start date are required." });
      return;
    }
    setSaving(true);
    try {
      await apiClient.put(`/subscriptions/${s.id}`, {
        customer_id: s.customer_id,
        plan_id: Number(form.plan_id),
        start_date: form.start_date,
        end_date: preview.end_date || s.end_date || null,
        type: Number(form.type),
        status: Number(form.status),
        delivery_notes: form.delivery_notes || null,
        internal_notes: form.internal_notes || null,
        packaging_notes: form.packaging_notes || null,
        deactivation_reason: s.deactivation_reason,
        addon_ids: form.addon_ids,
        deliver_days: form.day_names,
      });
      toast.success("Subscription updated");
      setEditing(false); setForm(null); setShowAllAddons(false);
      onSaved && onSaved();
    } catch (error) {
      setErrors(getFieldErrors(error));
      toast.error(getApiErrorMessage(error, "Could not save subscription."));
    } finally {
      setSaving(false);
    }
  };

  const addonList = [...(showAllAddons || editing ? addons : (s.addons || []))].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
  );
  const addonSelected = editing ? form.addon_ids : (s.addons || []).map((a) => a.id);

  return (
    <Card className="mb-6" data-testid="subscription-info-card">
      <CardHeader className="flex-col items-start gap-3 border-b border-border pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <CardTitle className="truncate">{editing ? "Edit Subscription" : (s.plan_name || `Plan #${s.plan_id}`)}</CardTitle>
          {!editing && <StatusBadge label={meta.label} variant={meta.variant} />}
          {!editing && isCurrentlyPaused && <StatusBadge label="Paused" variant="warning" />}
        </div>
        {editing ? (
          <div className="flex items-center gap-2" data-testid="subscription-info-edit-actions">
            <Button variant="secondary" size="sm" onClick={cancelEdit} data-testid="subscription-edit-cancel-button"><X size={15} strokeWidth={1.5} /> Cancel</Button>
            <Button variant="accent" size="sm" onClick={save} disabled={saving} data-testid="subscription-edit-save-button"><Check size={15} strokeWidth={1.5} /> {saving ? "Saving..." : "Save Changes"}</Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2" data-testid="subscription-info-actions">
            <Button variant="outline" size="sm" onClick={enterEdit} disabled={isDeleted} data-testid="subscription-detail-edit-button"><Pencil size={15} strokeWidth={1.5} /> Edit</Button>
            {hasPause ? (
              <Button variant="outline" size="sm" onClick={() => onAction("resume")} disabled={isDeleted} data-testid="subscription-detail-resume-button"><Play size={15} strokeWidth={1.5} /> Resume</Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => onAction("pause")} disabled={isDeleted} data-testid="subscription-detail-pause-button"><Pause size={15} strokeWidth={1.5} /> Pause</Button>
            )}
            {canRenew && (
              <Button variant="outline" size="sm" onClick={() => onAction("renew")} disabled={isDeleted} data-testid="subscription-detail-renew-button"><RefreshCw size={15} strokeWidth={1.5} /> Renew</Button>
            )}
            <Button variant="outline" size="sm" onClick={() => onAction("bonus")} disabled={isDeleted} data-testid="subscription-detail-bonus-button"><Gift size={15} strokeWidth={1.5} /> Add Bonus Tiffin</Button>
            {isDeactivated ? (
              <Button variant="outline" size="sm" onClick={() => onAction("reactivate")} data-testid="subscription-detail-reactivate-button"><RotateCcw size={15} strokeWidth={1.5} /> Reactivate</Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => onAction("deactivate")} disabled={!isActiveOrPending} data-testid="subscription-detail-deactivate-button"><Ban size={15} strokeWidth={1.5} /> Deactivate</Button>
            )}
            <Button variant="danger" size="sm" onClick={() => onAction("delete")} disabled={isDeleted} data-testid="subscription-detail-delete-button"><Trash2 size={15} strokeWidth={1.5} /> Delete</Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="grid grid-cols-2 gap-x-4 gap-y-5 pt-4 sm:grid-cols-3 lg:grid-cols-4">
        <Field label="Plan" testId="subscription-detail-plan">
          {editing ? (
            <Select value={form.plan_id} onValueChange={(v) => setForm((f) => ({ ...f, plan_id: v }))}>
              <SelectTrigger className="h-9" data-testid="subscription-edit-plan-select"><SelectValue placeholder="Select plan..." /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name} — {formatCurrency(p.price)}{p.status !== 1 ? " (Inactive)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (s.plan_name || `#${s.plan_id}`)}
        </Field>

        <Field label="Type" testId="subscription-detail-type">
          {editing ? (
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger className="h-9" data-testid="subscription-edit-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Pickup</SelectItem>
                <SelectItem value="2">Delivery</SelectItem>
              </SelectContent>
            </Select>
          ) : (s.type_label || "—")}
        </Field>

        <Field label="Status" testId="subscription-detail-status">
          {editing ? (
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="h-9" data-testid="subscription-edit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Active</SelectItem>
                <SelectItem value="2">Pending</SelectItem>
                <SelectItem value="4">Expired</SelectItem>
                <SelectItem value="3">Deactivated</SelectItem>
              </SelectContent>
            </Select>
          ) : (<StatusBadge label={meta.label} variant={meta.variant} />)}
        </Field>

        <Field label="Start Date" testId="subscription-detail-start-date">
          {editing ? (
            <Input type="date" className="h-9" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="subscription-edit-start-date" />
          ) : formatDate(s.start_date)}
        </Field>

        <Field label={editing ? "Projected End Date" : "End Date"} testId="subscription-detail-end-date">
          {editing ? <span className={preview.loading ? "text-text-muted" : ""}>{preview.loading ? "Updating…" : formatDate(preview.end_date) || "—"}</span> : formatDate(s.end_date)}
        </Field>

        <Field label="Price" testId="subscription-detail-price">
          {editing ? <span className={preview.loading ? "text-text-muted" : ""}>{preview.loading ? "Updating…" : (preview.price != null ? formatCurrency(preview.price) : "—")}</span> : formatCurrency(s.price)}
        </Field>

        <Field label={editing ? "Projected Deliveries" : "Deliveries Planned"} testId="subscription-detail-deliveries-count">
          {editing
            ? (preview.loading ? "Updating…" : (preview.deliveries_total ?? "—"))
            : (s.deliveries_count ? `${s.deliveries_count.delivery_total - s.deliveries_count.delivery_pending} of ${s.deliveries_count.delivery_total} generated (${deliveredPct}%)` : "—")}
        </Field>

        {!editing && s.bonus_tiffins > 0 && (
          <Field label="Bonus Tiffins" testId="subscription-detail-bonus-tiffins">
            <span className="inline-flex items-center gap-1 text-accent-hover"><Gift size={13} strokeWidth={1.5} /> {s.bonus_tiffins} bonus tiffin{s.bonus_tiffins === 1 ? "" : "s"}</span>
          </Field>
        )}
        {!editing && isDeactivated && <Field label="Deactivation Reason" testId="subscription-detail-deactivation-reason">{s.deactivation_reason || "—"}</Field>}

        <Field label="Delivery Days" testId="subscription-detail-deliver-days" className="col-span-2 sm:col-span-3 lg:col-span-4">
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(DAY_LABELS).map((d) => {
              const on = editing ? form.day_names.includes(d) : selectedDays.includes(d);
              const closed = editing && !outletDays[d];
              return (
                <button
                  key={d}
                  type="button"
                  disabled={!editing || closed}
                  onClick={() => toggleDay(d)}
                  title={closed ? "Outlet closed this day" : undefined}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    on ? "border-primary/30 bg-primary/10 text-primary" : "border-border bg-background text-text-muted"
                  } ${editing && !closed ? "cursor-pointer hover:border-primary/50" : ""} ${closed ? "opacity-40" : ""}`}
                  data-testid={`subscription-day-${d}`}
                >
                  {DAY_LABELS[d]}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Addons" testId="subscription-detail-addons" className="col-span-2 sm:col-span-3 lg:col-span-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {addonList.length === 0 && !editing && <span className="text-text-muted">No addons</span>}
            {addonList.map((a) => {
              const on = addonSelected.includes(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  disabled={!editing}
                  onClick={() => toggleAddon(a.id)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    on ? "border-accent/40 bg-accent/10 text-accent-hover" : "border-border bg-background text-text-muted"
                  } ${editing ? "cursor-pointer hover:border-accent/60" : ""}`}
                  data-testid={`subscription-addon-${a.id}`}
                >
                  {a.name} · {formatCurrency(a.price)}
                </button>
              );
            })}
            {!editing && (
              <button
                type="button"
                onClick={async () => { if (!showAllAddons) await loadOptions(); setShowAllAddons((v) => !v); }}
                className="rounded-full px-2 py-0.5 text-xs font-semibold text-primary hover:underline"
                data-testid="subscription-addons-show-all"
              >
                {showAllAddons ? "Show selected only" : "Show all add-ons"}
              </button>
            )}
          </div>
        </Field>

        {editing ? (
          <>
            <Field label="Delivery Notes" testId="subscription-detail-delivery-notes" className="col-span-full">
              <Textarea rows={5} value={form.delivery_notes} onChange={(e) => setForm((f) => ({ ...f, delivery_notes: e.target.value }))} data-testid="subscription-edit-delivery-notes" />
            </Field>
            <Field label="Internal Notes" testId="subscription-detail-internal-notes" className="col-span-full">
              <Textarea rows={5} value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} data-testid="subscription-edit-internal-notes" />
            </Field>
            <Field label="Packaging Notes" testId="subscription-detail-packaging-notes" className="col-span-full">
              <Textarea rows={5} value={form.packaging_notes} onChange={(e) => setForm((f) => ({ ...f, packaging_notes: e.target.value }))} data-testid="subscription-edit-packaging-notes" />
            </Field>
          </>
        ) : (s.delivery_notes || s.internal_notes || s.packaging_notes) && (
          <>
            <Field label="Delivery Notes" testId="subscription-detail-delivery-notes">{s.delivery_notes || "—"}</Field>
            <Field label="Internal Notes" testId="subscription-detail-internal-notes">{s.internal_notes || "—"}</Field>
            <Field label="Packaging Notes" testId="subscription-detail-packaging-notes">{s.packaging_notes || "—"}</Field>
          </>
        )}

        {errors.form && <p className="col-span-full text-sm text-danger" data-testid="subscription-edit-error">{errors.form}</p>}
      </CardContent>
    </Card>
  );
}
